import { useContext, useEffect, useMemo, useState } from "react";
import Router, { useRouter } from "next/router";
import {
  useChangeToAdminMutation,
  useVerifyChangeToAdminTokenMutation,
} from "@/generated/graphql";
import { getUrlWithQuery } from "@/libs/url";
import { ErrorCode, errorCodeIsIncluded, moveToErrorPage } from "@/libs/errors";
import { css } from "@emotion/react";

import { MainTemplate } from "@/components/templates";
import {
  ProvisionalRegistrationTerms,
  ProvisionalRegistrationTermsValues,
} from "@/components/organisms";
import { PageTitle } from "@/components/molecules";
import { Font, useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { AuthContext, AuthStatus } from "@/contexts/AuthContext";
import {
  clearLoginCompletedSnackbar,
  hasLoginCompletedSnackbar,
  LOGIN_COMPLETED_QUERY_PARAM,
} from "@/libs/loginCompletedSnackbar";
import useRollbarUtil from "../hooks/useRollbarUtil";

export const ChangeToAdminPage = () => {
  const { rollbar } = useRollbarUtil();
  const { notify } = useSnackBar();
  const router = useRouter();
  const { authStatus } = useContext(AuthContext);
  const [verifying, setVerifying] = useState(true);

  const token = useMemo(() => {
    return router.query.token as string;
  }, [router.query.token]);
  const { app } = router.query;

  const [verifyChangeToAdminTokenMutation] =
    useVerifyChangeToAdminTokenMutation({
      onCompleted: (data) => {
        if (data.verifyChangeToAdminToken.result) {
          setVerifying(false);
        } else {
          moveToErrorPage(ErrorCode.FAILED_TO_VERIFY_EMAIL_ADDRESS);
        }
      },
      onError: (error) => {
        if (errorCodeIsIncluded(error, ErrorCode.SIGNED_IN_AS_ANOTHER_USER)) {
          Router.push(
            getUrlWithQuery("/change-to-admin-another-user", app, {
              token: token,
            })
          );
        } else {
          rollbar.error("Failed to verify change to admin token", error);
          moveToErrorPage(ErrorCode.FAILED_TO_VERIFY_EMAIL_ADDRESS);
        }
      },
    });

  const [changeToAdminMutation] = useChangeToAdminMutation({
    onCompleted: (data) => {
      if (data.changeToAdmin.result) {
        Router.push(getUrlWithQuery("/change-to-admin-completed", app));
      }
    },
    onError: (error) => {
      if (errorCodeIsIncluded(error, ErrorCode.SIGNED_IN_AS_ANOTHER_USER)) {
        Router.push(
          getUrlWithQuery("/change-to-admin-another-user", app, {
            token: token,
          })
        );
      } else {
        rollbar.error("Failed to change to admin", error);
        moveToErrorPage(ErrorCode.FAILED_TO_VERIFY_EMAIL_ADDRESS);
      }
    },
  });

  useEffect(() => {
    if (!router.isReady || verifying) return;

    const hasQueryFlag = router.query[LOGIN_COMPLETED_QUERY_PARAM] === "1";
    if (!hasQueryFlag && !hasLoginCompletedSnackbar()) return;

    notify("ログインが完了しました");
    clearLoginCompletedSnackbar();

    if (hasQueryFlag) {
      const { [LOGIN_COMPLETED_QUERY_PARAM]: _removed, ...rest } = router.query;
      router.replace({ pathname: router.pathname, query: rest }, undefined, {
        shallow: true,
      });
    }
  }, [router.isReady, verifying, notify, router.query]);

  useEffect(() => {
    if (!token) return;
    if (authStatus === AuthStatus.SignedIn) {
      verifyChangeToAdminTokenMutation({ variables: { input: { token } } });
    }
  }, [token, authStatus]);

  const handleSubmit = async (
    formValues: ProvisionalRegistrationTermsValues
  ) => {
    changeToAdminMutation({ variables: { input: { token } } });
  };

  return (
    <MainTemplate title="規約への同意">
      {verifying ? (
        <div css={messageWrapper}>
          <Font variant="m" component="p">
            確認中...
          </Font>
        </div>
      ) : (
        <div css={contentsWrapper}>
          <PageTitle mainTitle={`規約への同意`} />
          <ProvisionalRegistrationTerms onSubmit={handleSubmit} />
        </div>
      )}
    </MainTemplate>
  );
};
const contentsWrapper = css({
  margin: "16px auto",
});

const messageWrapper = css({
  display: "flex",
  justifyContent: "center",
});
